/**
 * RTWO Cryptography & Identity Engine — Orchestrator
 * Phase 7 | Module ID: RTWO-CRYP-001
 * Version: 1.0.0-phase7
 *
 * Coordinates all cryptography services through dependency injection,
 * in-memory repositories, provider registries, and the crypto event bus.
 *
 * Truthful Data Policy: provider scaffolds registered. No keys, wallets,
 * signatures, or hashes generated until real cryptographic implementations connect.
 */

import { CRYPTOGRAPHY_ENGINE_VERSION, KEY_LIFECYCLE, IDENTITY_LIFECYCLE } from './domain-models';
import type { CryptoEngineStatus } from './domain-models';
import type {
  ICryptoEngineOrchestrator,
  ICryptoServiceRegistry,
  IKeyPairGenerationService,
  IAddressDerivationService,
  ISignatureCreationService,
  ISignatureVerificationService,
  IPublicKeyValidationService,
  IIdentityRegistrationService,
  IIdentityVerificationService,
  IKeyRotationService,
  IKeyStoragePolicyService,
  ITrustRecordService,
  ICryptoEventPublishingService,
  IProviderLifecycleService,
  IProviderRegistrationService,
  IConfigurationBindingService,
  IRepositoryBootstrapService,
} from './service-interfaces';
import { CryptoServiceRegistry } from './registry/crypto-service-registry';
import { createCryptoEngineServices } from './services';

export class RTWOCryptographyEngine implements ICryptoEngineOrchestrator {
  private static _instance: RTWOCryptographyEngine | null = null;

  readonly engineId = 'RTWO-CRYP-001';
  readonly engineName = CRYPTOGRAPHY_ENGINE_VERSION.moduleName;
  readonly version = CRYPTOGRAPHY_ENGINE_VERSION.version;

  private _status: CryptoEngineStatus = 'structure_ready';
  private readonly _serviceRegistry = new CryptoServiceRegistry();

  private readonly _keyPairGenService: IKeyPairGenerationService;
  private readonly _addressDerivationService: IAddressDerivationService;
  private readonly _signatureCreationService: ISignatureCreationService;
  private readonly _signatureVerificationService: ISignatureVerificationService;
  private readonly _publicKeyValidationService: IPublicKeyValidationService;
  private readonly _identityRegistrationService: IIdentityRegistrationService;
  private readonly _identityVerificationService: IIdentityVerificationService;
  private readonly _keyRotationService: IKeyRotationService;
  private readonly _keyStoragePolicyService: IKeyStoragePolicyService;
  private readonly _trustRecordService: ITrustRecordService;
  private readonly _eventPublishingService: ICryptoEventPublishingService;
  private readonly _providerLifecycleService: IProviderLifecycleService;
  private readonly _providerRegistrationService: IProviderRegistrationService;
  private readonly _configurationBindingService: IConfigurationBindingService;
  private readonly _repositoryBootstrapService: IRepositoryBootstrapService;

  private readonly _repositories;
  private readonly _keyProviderRegistry;
  private readonly _identityBackendRegistry;

  private constructor() {
    const { repositories, keyProviderRegistry, identityBackendRegistry, services } =
      createCryptoEngineServices();

    this._repositories = repositories;
    this._keyProviderRegistry = keyProviderRegistry;
    this._identityBackendRegistry = identityBackendRegistry;

    this._keyPairGenService = services.keyPairGeneration;
    this._addressDerivationService = services.addressDerivation;
    this._signatureCreationService = services.signatureCreation;
    this._signatureVerificationService = services.signatureVerification;
    this._publicKeyValidationService = services.publicKeyValidation;
    this._identityRegistrationService = services.identityRegistration;
    this._identityVerificationService = services.identityVerification;
    this._keyRotationService = services.keyRotation;
    this._keyStoragePolicyService = services.keyStoragePolicy;
    this._trustRecordService = services.trustRecord;
    this._eventPublishingService = services.eventPublishing;
    this._providerLifecycleService = services.providerLifecycle;
    this._providerRegistrationService = services.providerRegistration;
    this._configurationBindingService = services.configurationBinding;
    this._repositoryBootstrapService = services.repositoryBootstrap;

    const serviceList = [
      this._keyPairGenService,
      this._addressDerivationService,
      this._signatureCreationService,
      this._signatureVerificationService,
      this._publicKeyValidationService,
      this._identityRegistrationService,
      this._identityVerificationService,
      this._keyRotationService,
      this._keyStoragePolicyService,
      this._trustRecordService,
      this._eventPublishingService,
      this._providerLifecycleService,
      this._providerRegistrationService,
      this._configurationBindingService,
      this._repositoryBootstrapService,
    ];

    for (const service of serviceList) {
      this._serviceRegistry.register(service);
    }

    void Promise.all(serviceList.map((service) => service.initialize())).then(() => {
      this._status = this.resolveEngineStatus();
    });
  }

  private resolveEngineStatus(): CryptoEngineStatus {
    const keyProviderConnected = this._keyProviderRegistry.isConfigured();
    const identityBackendConnected = this._identityBackendRegistry.isConfigured();

    if (keyProviderConnected && identityBackendConnected) {
      return 'operational';
    }

    if (keyProviderConnected) {
      return 'key_provider_connected';
    }

    return 'waiting_for_key_provider';
  }

  static getInstance(): RTWOCryptographyEngine {
    if (!RTWOCryptographyEngine._instance) {
      RTWOCryptographyEngine._instance = new RTWOCryptographyEngine();
    }
    return RTWOCryptographyEngine._instance;
  }

  getStatus(): CryptoEngineStatus {
    return this._status;
  }

  getServiceRegistry(): ICryptoServiceRegistry {
    return this._serviceRegistry;
  }

  getKeyPairGenerationService(): IKeyPairGenerationService {
    return this._keyPairGenService;
  }

  getAddressDerivationService(): IAddressDerivationService {
    return this._addressDerivationService;
  }

  getSignatureCreationService(): ISignatureCreationService {
    return this._signatureCreationService;
  }

  getSignatureVerificationService(): ISignatureVerificationService {
    return this._signatureVerificationService;
  }

  getPublicKeyValidationService(): IPublicKeyValidationService {
    return this._publicKeyValidationService;
  }

  getIdentityRegistrationService(): IIdentityRegistrationService {
    return this._identityRegistrationService;
  }

  getIdentityVerificationService(): IIdentityVerificationService {
    return this._identityVerificationService;
  }

  getKeyRotationService(): IKeyRotationService {
    return this._keyRotationService;
  }

  getKeyStoragePolicyService(): IKeyStoragePolicyService {
    return this._keyStoragePolicyService;
  }

  getTrustRecordService(): ITrustRecordService {
    return this._trustRecordService;
  }

  getEventPublishingService(): ICryptoEventPublishingService {
    return this._eventPublishingService;
  }

  getProviderLifecycleService(): IProviderLifecycleService {
    return this._providerLifecycleService;
  }

  getProviderRegistrationService(): IProviderRegistrationService {
    return this._providerRegistrationService;
  }

  getConfigurationBindingService(): IConfigurationBindingService {
    return this._configurationBindingService;
  }

  getRepositoryBootstrapService(): IRepositoryBootstrapService {
    return this._repositoryBootstrapService;
  }

  getEngineSummary() {
    const keyProviderConnected = this._keyProviderRegistry.isConfigured();
    const identityBackendConnected = this._identityBackendRegistry.isConfigured();

    return {
      engineId: this.engineId,
      engineName: this.engineName,
      version: this.version,
      phase: CRYPTOGRAPHY_ENGINE_VERSION.phase,
      status: this._status,
      productionReadiness: CRYPTOGRAPHY_ENGINE_VERSION.productionReadiness,
      totalServices: this._serviceRegistry.count(),
      keyProviderConnected,
      identityBackendConnected,
      realKeysGenerated: keyProviderConnected ? this._repositories.keyPairs.count() : 0,
      realAddressesDerived: keyProviderConnected ? this._repositories.addresses.count() : 0,
      realIdentitiesRegistered: identityBackendConnected
        ? this._repositories.identities.count()
        : 0,
      policy: CRYPTOGRAPHY_ENGINE_VERSION.policy,
      integrations: {
        coreEngine: 'Structure Complete',
        blockchainProtocolEngine: 'Structure Complete',
        consensusEngine: 'Structure Complete',
        ledgerStateEngine: 'Structure Complete',
        p2pNetworkEngine: 'Structure Complete',
        systemRegistry: 'Registered',
        truthfulStatusManager: 'Active',
        r6SecurityAutomation: keyProviderConnected ? 'Connected' : 'Not Configured',
        r7EnterpriseServices: identityBackendConnected ? 'Connected' : 'Not Configured',
      },
    };
  }
}

export const getCryptographyEngine = (): RTWOCryptographyEngine =>
  RTWOCryptographyEngine.getInstance();

export { KEY_LIFECYCLE, IDENTITY_LIFECYCLE };
